import React from 'react'
import { logger } from '../logging'

import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client'
import { Vpn, VpnMetrics, VpnState } from '@mytmpvpn/mytmpvpn-common/models/vpn'
import { MyTmpVpnError } from '@mytmpvpn/mytmpvpn-common/errors'
import { useErrorContext } from '../ErrorContext'
import { PageContainer, reactUpdateVpnWithMetrics } from '../components/Common'
import { Dashboard } from './Dashboard'
import { Welcome } from './Welcome'
import { Locations, LocationsSkeleton } from './Locations'
import { populateClientWithSesion, isClientAuthenticated } from '../utils/authUtils'
import { Divider } from '@aws-amplify/ui-react'
import { InstanceType, LocationWithProvidersInfo } from '@mytmpvpn/mytmpvpn-common/models/location'
import { VpnConfig, VpnType } from '@mytmpvpn/mytmpvpn-common/models/vpnConfig'
import { useAuthenticator } from '../amplify'
import { isTerminalState } from '../components/vpns/VpnState'

export interface VpnWithMetrics extends Vpn {
    metrics: VpnMetrics | undefined
    pollCount?: number
}

function resumePollingForNonTerminalVpns(
    client: MyTmpVpnClient,
    vpns: VpnWithMetrics[],
    setVpnsWithMetrics: any,
    setError: any
) {
    for (const vpn of vpns) {
        if (isTerminalState(vpn.state)) continue

        const targetState = vpn.state === VpnState.Deprovisioning ? VpnState.Deleted : VpnState.Running
        logger.log(`Resuming polling for vpn ${vpn.vpnId} towards ${targetState}`)

        client.waitUntilVpnStateIs(vpn.vpnId, targetState, 300, (polledVpn: Vpn, pollCount: number) => {
            setVpnsWithMetrics((prevVpns: VpnWithMetrics[]) => {
                return reactUpdateVpnWithMetrics(prevVpns, {
                    ...polledVpn,
                    metrics: vpn.metrics,
                    pollCount
                })
            })
        }).then(async (finishedVpn) => {
            logger.log(`Resumed polling: vpn ${finishedVpn.vpnId} reached ${finishedVpn.state}`)
            try {
                const response = await client.getVpn(finishedVpn.vpnId)
                setVpnsWithMetrics((prevVpns: VpnWithMetrics[]) => {
                    return reactUpdateVpnWithMetrics(prevVpns, {
                        ...response.vpn,
                        metrics: response.metrics
                    })
                })
            } catch (e) {
                logger.error(`Error fetching vpn after polling: ${e}`)
            }
        }).catch((e) => {
            logger.error(`Error during resumed polling for ${vpn.vpnId}: ${e}`)
            setError(e)
        })
    }
}

export async function listAllVpns(client: MyTmpVpnClient,
    setVpnsWithMetrics: any,
    setError: any) {

    if (!isClientAuthenticated(client)) {
        return undefined
    }
    try {
        // Fetch all VPNs without pagination for client-side processing
        const listResponse = await client.listVpnsPaginated({
            pageSize: 100, // Use a large page size to get all VPNs
            // No filtering or sorting on server side - we'll do it client-side
        })

        const vpns = listResponse.vpns
        logger.log(`Response to list all vpns: ${JSON.stringify(listResponse)}`)

        const errors: any = []
        const vpnPromises = vpns.map(async (vpn) => {
            try {
                const getResponse = await client.getVpn(vpn.vpnId)
                logger.log('Fetched vpn: ', getResponse)
                return {
                    ...vpn,
                    metrics: getResponse.metrics
                }
            } catch (err) {
                errors.push(err)
                // Ensure createdAt is converted to Date even on error
                return { ...vpn, metrics: undefined } as VpnWithMetrics
            }
        })
        logger.log('Fetching metrics for ', vpns.length, ' vpns: ', vpnPromises)
        const result: VpnWithMetrics[] = (await Promise.all(vpnPromises))
        setVpnsWithMetrics(result)
        if (errors.length > 0) {
            logger.error(`Failed to fetch metrics for ${errors.length} vpns: ${JSON.stringify(errors)}`)
            throw new MyTmpVpnError(`${errors.length} errors occured while listing vpns. See the console log for details`)
        }
        return result
    } catch (err) {
        setError(err)
    }
}

export function Home({ client }: { client: MyTmpVpnClient }) {
    const { setError } = useErrorContext()
    const { user, authStatus } = useAuthenticator((context) => [context.user, context.authStatus])
    const [balance, setBalance] = React.useState<number>(Number.NaN)
    const [vpnConfigLimits, setVpnConfigLimits] = React.useState<any>()
    const [vpnConfig, setVpnConfig] = React.useState<VpnConfig>({
        type: VpnType.WireGuard,
        maxPeanuts: 0,
        port: 0,
        instanceType: InstanceType.Fast
    } as VpnConfig)
    const [vpnsWithMetrics, setVpnsWithMetrics] = React.useState<VpnWithMetrics[]>([])
    // Removed pagination-related state since we're doing client-side pagination
    const [locations, setLocations] = React.useState<LocationWithProvidersInfo[]>([])
    const [displayInstructions, setDisplayInstructions] = React.useState<boolean>(true)
    const [isDashboardLoading, setIsDashboardLoading] = React.useState<boolean>(true)
    const [isLocationsLoading, setIsLocationsLoading] = React.useState<boolean>(true)


    React.useEffect(() => {
        // Fetch locations independently (no auth required) — resolves fast
        client.listLocations().then(locations => {
            logger.log(`Setting locations: ${JSON.stringify(locations)}`)
            setLocations(locations)
            setIsLocationsLoading(false)
        }).catch(err => {
            setError(err)
            setIsLocationsLoading(false)
        })
    }, [])

    React.useEffect(() => {
        const fetchData = async () => {
            try {

                // Fetch the current session only if user is authenticated
                if (authStatus === 'authenticated') {
                    logger.debug(`Authenticated: ${authStatus}, fetching user specific data`)
                    await populateClientWithSesion(client, user)

                    // Fetching balance, vpns list and vpn config limits in parallel
                    const [vpns, balance, userConfig] = await Promise.all([
                        listAllVpns(client, setVpnsWithMetrics, setError),
                        client.getPeanutsBalance(),
                        client.getUserConfig(),
                    ])

                    // Resume polling for any VPNs still in non-terminal states
                    if (vpns) {
                        resumePollingForNonTerminalVpns(client, vpns, setVpnsWithMetrics, setError)
                    }

                    const limits = userConfig.vpnConfigLimits

                    logger.log(`Setting balance: ${JSON.stringify(balance)}`)
                    setBalance(balance)
                    logger.log(`Setting vpn config limits: ${JSON.stringify(limits)}`)
                    setVpnConfigLimits(limits)

                    const deleteAfterDefault = limits.deleteAfterFieldMinValue >= 5 * 60 // This is the usual case
                        ? 4 * 60 * 60 // We default to 4 hours in usual case 
                        : (limits.deleteAfterFieldMaxValue - limits.deleteAfterFieldMinValue) / 2
                    const newVpnConfig = {
                        ...vpnConfig,
                        deleteAfter: deleteAfterDefault
                    }
                    logger.log(`Setting vpn config: `, newVpnConfig)
                    setVpnConfig(newVpnConfig)

                    logger.log(`totalVpns for displayInstructions: `, vpns?.length)
                    // We only display instructions if customers have no vpn yet
                    setDisplayInstructions(vpns === undefined || vpns.length === 0)
                }
            } catch (error: any) {
                setError(error)
            } finally {
                setIsDashboardLoading(false)
            }
        }

        fetchData()
    }, [user, authStatus]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <PageContainer>
            {authStatus === 'authenticated' || authStatus === 'configuring' ?
                <Dashboard
                    client={client}

                    balance={balance}
                    setBalance={setBalance}

                    vpnConfigLimits={vpnConfigLimits}

                    vpnConfig={vpnConfig}
                    setVpnConfig={setVpnConfig}

                    vpnsWithMetrics={vpnsWithMetrics}
                    setVpnsWithMetrics={setVpnsWithMetrics}

                    isDashboardLoading={isDashboardLoading}

                    locations={locations}

                    displayInstructions={displayInstructions}
                    setDisplayInstructions={setDisplayInstructions}
                />
                : <Welcome client={client} />}
            <Divider size='large' padding={10} />
            {isLocationsLoading ? <LocationsSkeleton /> :
                <Locations
                    client={client}
                    vpnConfig={vpnConfig}
                    vpnConfigLimits={vpnConfigLimits}
                    setVpnsWithMetrics={setVpnsWithMetrics}
                    locations={locations}
                    balance={balance}
                />
            }
        </PageContainer>
    )
}
